import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import PrivacyOption from '../../util/PrivacyOption';
import TabContentWrapper from '../TabContentWrapper';
import {MediaPrivacy} from '../../../model/mediaDetails';
import mediaApi from '../../../service/mediaApi';
import {IWorkerState} from '../../../model/workerState';
import ConfirmPrivacyChangeModal from '../../modals/ConfirmPrivacyChangeModal';
import {CaptioningTypes, ICaptionData} from '../../../model/captionModels';
import {getMediaPrivacyOptions} from '../../../util/MediaPrivacyOptionsGenerator';
import {MediaPrivacyOptionModel} from '../../../model/mediaPrivacyOptionModel';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {useTscTheme} from '../../../context/themeProvider';
import {mediaStore, updateMediaState} from '../../../context/mediaProvider';
import {captionDataStore} from '../../../context/captionDataProvider';
import {IBaseLibrary} from '../../../model/library';

const MediaPrivacyHeader = styled.h2`
   font-size: 0.875rem;
   font-weight: 600;
   margin-top: 0;
`;

const MediaPrivacyTabWrapper = styled.div`
   display: flex;
   flex-direction: column;
`;

const PrivacyLevelWrapper = styled.div`
   width: 100%;
`;

export const MediaPrivacySettings: React.FC<IStateMappedProps> = ({Hash, OwnerLibrary, CaptioningAssignedToEmail, GroupHashes, Privacy, captionData}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [showPrivateConfirmationModal, setShowPrivateConfirmationModal] = useState(false);
   const [warningMessages, setWarningMessages] = useState([]);
   const [privacyOptions] = useState<Map<MediaPrivacy, MediaPrivacyOptionModel>>(getMediaPrivacyOptions(OwnerLibrary.LibraryType));

   const [state, setState] = React.useState<IWorkerState>({
      isWorking: false,
      hasError: false
   });

   React.useEffect(() => {
      const messages = [];
      if (CaptioningAssignedToEmail) {
         messages.push(t('confirmPrivacyChange.assignee'));
      }
      if (GroupHashes.length > 0) {
         messages.push(t('collections.privacyChange'));
      }
      if (captionData && captionData.filter(c => c.CaptioningType === CaptioningTypes.ThirdParty).length > 0) {
         messages.push(t('confirmPrivacyChange.captions'));
      }

      setWarningMessages(messages);
   }, [Hash, captionData]);

   const updatePrivacy = async (newPrivacy: MediaPrivacy) => {
      if (Privacy !== newPrivacy) {
         setState({...state, isWorking: true});
         try {
            await mediaApi.updatePrivacy(Hash, newPrivacy);
            updateMediaState({Privacy: newPrivacy});
            setState({isWorking: false, hasError: false});
         } catch {
            setState({isWorking: false, hasError: true});
         }
      }
   };

   const onClickPrivateOption = async () => {
      if (warningMessages.length > 0) {
         setShowPrivateConfirmationModal(true);
      } else {
         await updatePrivacy(MediaPrivacy.private);
      }
   };

   const onConfirmClick = async () => {
      setShowPrivateConfirmationModal(false);
      await updatePrivacy(MediaPrivacy.private);
   };

   const onCloseClick = () => {
      setShowPrivateConfirmationModal(false);
   };

   return (
      <TabContentWrapper
         showSpinner={state.isWorking}
         showError={state.hasError}
      >
         <MediaPrivacyTabWrapper
            data-test-id="privacy-tab-wrapper"
            className="t-privacy-tab"
         >
            <PrivacyLevelWrapper>
               <MediaPrivacyHeader>
                  {t('privacy.media.tabHeader')}
               </MediaPrivacyHeader>
               <PrivacyOption
                  onClick={() => updatePrivacy(MediaPrivacy.public)}
                  selected={Privacy === MediaPrivacy.public}
                  header={privacyOptions.get(MediaPrivacy.public).label}
                  text={privacyOptions.get(MediaPrivacy.public).subtext}
                  glyph={privacyOptions.get(MediaPrivacy.public).glyph}
                  radioTestId="public-media-privacy"
                  theme={theme}
                  disabled={privacyOptions.get(MediaPrivacy.public).disabled}
                  titleBadgeText={privacyOptions.get(MediaPrivacy.public).badgeText}
               />
               <PrivacyOption
                  onClick={onClickPrivateOption}
                  selected={Privacy === MediaPrivacy.private}
                  header={privacyOptions.get(MediaPrivacy.private).label}
                  text={privacyOptions.get(MediaPrivacy.private).subtext}
                  glyph={privacyOptions.get(MediaPrivacy.private).glyph}
                  radioTestId="private-media-privacy"
                  theme={theme}
               />
            </PrivacyLevelWrapper>
         </MediaPrivacyTabWrapper>
         {showPrivateConfirmationModal && (
            <ConfirmPrivacyChangeModal
               onClose={onCloseClick}
               onConfirm={onConfirmClick}
               warnings={warningMessages}
            />
         )}
      </TabContentWrapper>
   );
};

export interface IStateMappedProps {
   Hash: string;
   OwnerLibrary: IBaseLibrary;
   CaptioningAssignedToEmail: string;
   GroupHashes: string[];
   Privacy: MediaPrivacy;
   captionData: ICaptionData[];
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {Hash, OwnerLibrary, CaptioningAssignedToEmail, GroupHashes, Privacy}: Partial<IStateMappedProps>,
   {captionData}: Partial<IStateMappedProps>
): IStateMappedProps => ({Hash, OwnerLibrary, CaptioningAssignedToEmail, GroupHashes, Privacy, captionData});

export default withMemoizedContexts(mapStatesToProps, mediaStore, captionDataStore)(MediaPrivacySettings);
